import React from "react";
import { LandingNavbar } from "@/components/landing-navbar";
import { Footer } from "@/components/footer";
import { SEO, schemas } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { ArrowRight, Calendar, MapPin, Check, Clock } from "lucide-react";
import { Link } from "react-router-dom";

export default function ServiceSchedulingPage() {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Service Scheduling Software for Cleaning Companies"
        description="Coordinate your cleaning team with a centralized calendar. Project scheduling, staff assignments, and real-time status tracking with ManageClean."
        keywords={[
          "cleaning scheduling software",
          "janitorial scheduling",
          "service scheduler",
          "cleaning calendar software",
        ]}
        url="/features/service-scheduling"
        structuredData={[
          schemas.createBreadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "Features", url: "/features/service-scheduling" },
            { name: "Service Scheduling", url: "/features/service-scheduling" },
          ]),
          schemas.createFeatureModuleSchema({
            name: "Service Scheduling & Dispatch",
            description:
              "Coordinate commercial cleaning crews with recurring contract calendars, route planning, shift assignments, and real-time mobile check-ins.",
            url: "/features/service-scheduling",
            featureList: [
              "Recurring Contract Shift Scheduler",
              "Multi-Location Map & Route Optimization",
              "Real-Time Arrival & Departure Tracking",
              "Automated Client Shift Notifications",
              "Team Master Calendar with Role-Based Views",
            ],
          }),
        ]}
      />
      <LandingNavbar />

      {/* Hero Section */}
      <section className="relative overflow-hidden pb-20 pt-32">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center gap-12 lg:flex-row">
            <div className="space-y-8 lg:w-1/2">
              <div className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-sm font-medium text-blue-600">
                <Calendar className="h-4 w-4" />
                <span>Smart Service Scheduling</span>
              </div>

              <h1 className="text-4xl font-bold leading-[1.1] tracking-tight text-slate-900 md:text-6xl">
                Effortless{" "}
                <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                  Project Scheduling
                </span>
              </h1>

              <p className="max-w-xl text-lg leading-relaxed text-slate-600">
                Coordinate your team and projects with a centralized calendar. Ensure every shift is
                covered and every client is served on time.
              </p>

              <div className="flex flex-wrap gap-4">
                <Link to="/sign-up">
                  <Button
                    size="lg"
                    className="h-12 rounded-full bg-blue-600 px-8 text-lg hover:bg-blue-700"
                  >
                    Get Started Free <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>

            <div className="relative lg:w-1/2">
              <div className="relative aspect-[16/9] overflow-hidden rounded-2xl border border-slate-200/50 bg-white/50 shadow-2xl backdrop-blur-sm">
                <img
                  src="/assets/feature_scheduling.png"
                  alt="Scheduling Interface"
                  className="h-full w-full object-cover"
                />
              </div>
              {/* Decorative elements */}
              <div className="absolute -bottom-12 -right-12 -z-10 h-64 w-64 rounded-full bg-indigo-500/10 blur-3xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Feature Deep Dive */}
      <section className="bg-slate-50 py-20">
        <div className="container mx-auto px-4">
          <div className="mx-auto mb-16 max-w-3xl text-center">
            <h2 className="mb-4 text-3xl font-bold text-slate-900">Never Miss a Service</h2>
            <p className="text-slate-600">
              Complex schedules made simple. Visualize your operations for the day, week, or month
              at a glance.
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-3">
            <motion.div
              whileHover={{ y: -5 }}
              className="rounded-2xl border border-slate-100 bg-white p-8 shadow-sm"
            >
              <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-xl bg-blue-100">
                <Calendar className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="mb-3 text-xl font-bold text-slate-900">Unified Calendar</h3>
              <p className="mb-4 text-slate-600">
                See all your projects, one-off cleans, and team availability in one view. Drag and
                drop to reschedule instantly.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-sm text-slate-600">
                  <Check className="mr-2 h-4 w-4 text-green-500" /> Daily, Weekly, Monthly Views
                </li>
                <li className="flex items-center text-sm text-slate-600">
                  <Check className="mr-2 h-4 w-4 text-green-500" /> Conflicts Alerts
                </li>
              </ul>
            </motion.div>

            <motion.div
              whileHover={{ y: -5 }}
              className="rounded-2xl border border-slate-100 bg-white p-8 shadow-sm"
            >
              <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-100">
                <MapPin className="h-6 w-6 text-indigo-600" />
              </div>
              <h3 className="mb-3 text-xl font-bold text-slate-900">Project Management</h3>
              <p className="mb-4 text-slate-600">
                Track status across all locations. Know exactly what is "Scheduled," "In Progress,"
                or "Completed" in real-time.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-sm text-slate-600">
                  <Check className="mr-2 h-4 w-4 text-green-500" /> Status Tracking
                </li>
                <li className="flex items-center text-sm text-slate-600">
                  <Check className="mr-2 h-4 w-4 text-green-500" /> Location Details
                </li>
              </ul>
            </motion.div>

            <motion.div
              whileHover={{ y: -5 }}
              className="rounded-2xl border border-slate-100 bg-white p-8 shadow-sm"
            >
              <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-xl bg-purple-100">
                <Clock className="h-6 w-6 text-purple-600" />
              </div>
              <h3 className="mb-3 text-xl font-bold text-slate-900">Staff Assignment</h3>
              <p className="mb-4 text-slate-600">
                Easily assign employees or subcontractors to specific projects. Ensure the right
                people are at the right place.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-sm text-slate-600">
                  <Check className="mr-2 h-4 w-4 text-green-500" /> Employee & Subcontractor Support
                </li>
                <li className="flex items-center text-sm text-slate-600">
                  <Check className="mr-2 h-4 w-4 text-green-500" /> Clear Assignments
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
